// Customer account area (/account/). Everything here progressively enhances
// server-rendered markup, so the pages still work before (or without) JS.

// Signed-out visitors: the show to save is remembered here while they sign in
// (or register) at Keycloak, then saved on the first signed-in page.
const PENDING_SAVE_KEY = 'oltPendingSaveShow';
const PENDING_SAVE_TTL = 60 * 60 * 1000;

const rememberPendingSave = (showId) => {
    try {
        window.localStorage.setItem(PENDING_SAVE_KEY, JSON.stringify({ showId, at: Date.now() }));
    } catch (error) {
        // Storage blocked: they'll sign in and can tap the heart again.
    }
};

const takePendingSave = () => {
    try {
        const pending = JSON.parse(window.localStorage.getItem(PENDING_SAVE_KEY) || 'null');
        window.localStorage.removeItem(PENDING_SAVE_KEY);
        return pending && Date.now() - pending.at < PENDING_SAVE_TTL ? Number(pending.showId) : 0;
    } catch (error) {
        return 0;
    }
};

// The account's REST routes, as the signed-in customer. With a body it's a
// JSON POST.
const accountFetch = (url, body) => fetch(url, {
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': window.oltAccount.nonce, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
});

const postSaved = (showId, saved) => accountFetch(window.oltAccount.savedShowsUrl, { show_id: showId, saved });

// Keycloak sign-in or registration, returning to this page.
const authUrl = (base) => {
    const url = new URL(base, window.location.href);
    url.searchParams.set('redirect_to', window.location.href);
    return url.toString();
};

// Signed-out visitors: a heart opens a popup explaining that saving needs an
// account, and that this show will be saved once they have one. The show is
// only remembered when they choose an option, so closing the popup saves
// nothing later. Built once, on first use, as pages are cached.
let savePopup = null;

const buildSavePopup = (labels) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'olt-account-dialog olt-save-popup';
    dialog.setAttribute('aria-labelledby', 'olt-save-popup-title');
    dialog.setAttribute('aria-describedby', 'olt-save-popup-text');
    dialog.innerHTML = `
        <div class="olt-account-dialog__form">
            <header class="olt-account-dialog__header">
                <h2 class="olt-account-dialog__title" id="olt-save-popup-title"></h2>
                <button type="button" class="olt-account-dialog__close" data-olt-dialog-close>×</button>
            </header>
            <div class="olt-account-dialog__body olt-save-popup__body">
                <p class="olt-save-popup__text" id="olt-save-popup-text"></p>
                <div class="olt-account-actions olt-account-actions--block">
                    <a class="button olt-save-popup__register" data-olt-save-popup="registerUrl" data-event="save_show_register"></a>
                </div>
                <p class="olt-save-popup__sign-in"><span></span> <a data-olt-save-popup="signInUrl" data-event="save_show_sign_in"></a></p>
            </div>
        </div>`;
    // Labels are set as text, never markup: editors write them in the admin.
    dialog.querySelector('.olt-account-dialog__title').textContent = labels.title;
    dialog.querySelector('.olt-account-dialog__close').setAttribute('aria-label', labels.close);
    dialog.querySelector('.olt-save-popup__register').textContent = labels.register;
    dialog.querySelector('.olt-save-popup__sign-in span').textContent = labels.haveAccount;
    dialog.querySelector('[data-olt-save-popup="signInUrl"]').textContent = labels.signIn;

    dialog.addEventListener('click', (event) => {
        const link = event.target.closest('[data-olt-save-popup]');
        if (link) rememberPendingSave(Number(dialog.dataset.showId));
    });
    dialog.addEventListener('close', () => dialog.opener?.focus());

    document.body.append(dialog);
    return dialog;
};

const openSavePopup = (button) => {
    const settings = window.oltAccount;
    const showId = Number(button.dataset.oltSaveShow);

    // No popup support (or settings from an older cached page): straight to sign in.
    if (!settings.popup || typeof window.HTMLDialogElement !== 'function') {
        rememberPendingSave(showId);
        window.location.assign(authUrl(settings.signInUrl));
        return;
    }

    savePopup = savePopup || buildSavePopup(settings.popup);
    savePopup.dataset.showId = showId;
    savePopup.opener = button;
    savePopup.querySelector('.olt-save-popup__text').textContent = settings.popup.text.replace('{show}', button.dataset.name || 'this show');
    savePopup.querySelectorAll('[data-olt-save-popup]').forEach((link) => {
        link.href = authUrl(settings[link.dataset.oltSavePopup]);
        link.dataset.postId = showId;
    });
    savePopup.showModal();
};

// Saved-show hearts: toggle via the REST API, optimistic with rollback.
document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-olt-save-show]');
    if (!button || !window.oltAccount) return;

    // Hearts sit inside linked cards: don't follow the card link.
    event.preventDefault();
    event.stopPropagation();

    if (!window.oltAccount.loggedIn) {
        openSavePopup(button);
        return;
    }

    const saved = button.getAttribute('aria-pressed') !== 'true';
    const label = button.querySelector('[data-olt-save-label]');
    const render = (state) => {
        button.setAttribute('aria-pressed', state ? 'true' : 'false');
        button.classList.toggle('is-saved', state);
        if (label) label.textContent = state ? window.oltAccount.labels.saved : window.oltAccount.labels.save;
    };

    render(saved);
    button.disabled = true;

    try {
        const response = await postSaved(Number(button.dataset.oltSaveShow), saved);
        if (!response.ok) throw new Error(response.statusText);
        render((await response.json()).saved);
    } catch (error) {
        render(!saved);
    } finally {
        button.disabled = false;
    }
});

// Hearts across the site. Listing cards are rendered into cached HTML (and
// some arrive later via AJAX), so the buttons are added here from the saved
// list rather than by the server. Signed-out visitors see them all unsaved.
if (window.oltAccount) {
    const settings = window.oltAccount;
    let savedIds = null;

    const fetchSaved = () => accountFetch(settings.savedShowsUrl)
        .then((response) => (response.ok ? response.json() : { show_ids: [] }))
        .then((data) => new Set((data.show_ids || []).map(Number)))
        .catch(() => new Set());

    // Back from signing in with a show to save: save it before the list loads.
    const savePending = () => {
        const showId = takePendingSave();
        return showId ? postSaved(showId, true).catch(() => null) : Promise.resolve();
    };

    const loadSaved = () => {
        if (!savedIds) {
            savedIds = settings.loggedIn ? savePending().then(fetchSaved) : Promise.resolve(new Set());
        }
        return savedIds;
    };

    const makeButton = (showId, name, saved, withLabel) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `olt-save-show${withLabel ? ' olt-save-show--labelled' : ' olt-save-show--card'}${saved ? ' is-saved' : ''}`;
        button.dataset.oltSaveShow = showId;
        button.dataset.event = 'save_show';
        button.dataset.postId = showId;
        button.dataset.name = name;
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        button.setAttribute('aria-label', `${settings.loggedIn ? settings.labels.save : settings.labels.signIn}: ${name}`);
        if (!settings.loggedIn) button.title = settings.labels.signIn;
        button.innerHTML = '<span class="olt-save-show__icon" aria-hidden="true"></span>';
        if (withLabel) {
            const label = document.createElement('span');
            label.dataset.oltSaveLabel = '';
            label.textContent = saved ? settings.labels.saved : settings.labels.save;
            button.append(label);
        }
        return button;
    };

    const enhanceCards = async (root) => {
        const cards = [];
        if (root.matches && root.matches('.shows-grid-item[data-post-id]')) cards.push(root);
        if (root.querySelectorAll) cards.push(...root.querySelectorAll('.shows-grid-item[data-post-id]'));

        const pending = cards.filter((card) => !card.closest('.olt-account-show') && !card.querySelector('[data-olt-save-show]'));
        if (!pending.length) return;

        const ids = await loadSaved();
        pending.forEach((card) => {
            if (card.querySelector('[data-olt-save-show]')) return;
            const id = Number(card.dataset.postId);
            card.classList.add('olt-save-show-host');
            card.append(makeButton(id, card.dataset.name || '', ids.has(id), false));
        });
    };

    // First sign-in can land on onboarding rather than back where they were,
    // so a remembered show is saved even when the page has no hearts.
    try {
        if (settings.loggedIn && window.localStorage.getItem(PENDING_SAVE_KEY)) loadSaved();
    } catch (error) {
        // Storage blocked: nothing pending.
    }

    enhanceCards(document);
    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) enhanceCards(node);
        }));
    }).observe(document.body, { childList: true, subtree: true });

    // Show page: a labelled button pinned to the right of the title row. It's
    // positioned out of the flow so the title/price/venue layout is untouched.
    const details = settings.currentShowId && document.querySelector('.show-details__content');
    const title = details && details.querySelector('.show-details__title');
    if (title && !details.querySelector('.olt-save-show--labelled')) {
        loadSaved().then((ids) => {
            const id = Number(settings.currentShowId);
            details.classList.add('olt-save-show-host', 'olt-save-show-host--show');
            details.prepend(makeButton(id, title.firstChild.textContent.trim(), ids.has(id), true));
        });
    }
}

// Competition entry lightboxes (inc/account/competitions.php).
document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-olt-competition-open]');
    if (opener) {
        const dialog = document.getElementById(opener.dataset.oltCompetitionOpen);
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
        return;
    }

    const closer = event.target.closest('[data-olt-dialog-close]');
    if (closer) closer.closest('dialog')?.close();

    // A click on the backdrop (the dialog element itself) closes it.
    if (event.target.matches && event.target.matches('dialog.olt-account-dialog')) event.target.close();
});

document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-olt-competition-form]');
    if (!form || !window.oltAccount) return;

    event.preventDefault();
    if (!form.reportValidity()) return;

    const fields = {};
    [...form.elements].forEach((el) => {
        if (!el.name) return;
        if (el.type === 'checkbox') fields[el.name] = el.checked;
        else if (el.type === 'radio') { if (el.checked) fields[el.name] = el.value; }
        else fields[el.name] = el.value;
    });

    const submit = form.querySelector('[data-olt-competition-submit]');
    const message = form.querySelector('[data-olt-competition-message]');
    const show = (text, ok) => {
        message.hidden = false;
        message.textContent = text;
        message.classList.toggle('is-success', ok);
    };

    submit.disabled = true;

    try {
        const response = await accountFetch(form.dataset.endpoint, { fields });
        const data = await response.json();

        if (!response.ok) {
            show(data.message || form.dataset.errorLabel, false);
            submit.disabled = false;
            return;
        }

        show(data.message, true);
        submit.hidden = true;
        form.querySelectorAll('input, textarea').forEach((el) => { el.disabled = true; });
        document.querySelectorAll(`[data-olt-competition-open="${form.closest('dialog').id}"]`).forEach((button) => {
            const entered = document.createElement('p');
            entered.className = 'olt-account-competition__entered';
            entered.textContent = form.dataset.enteredLabel;
            button.replaceWith(entered);
        });
    } catch (error) {
        show(form.dataset.errorLabel, false);
        submit.disabled = false;
    }
});

// Toggles with sub-options (Access Information) reveal them while on;
// switching the parent off clears them so nothing hidden stays subscribed.
document.querySelectorAll('.olt-account-toggle__input[aria-controls]').forEach((toggle) => {
    const group = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!group) return;

    toggle.addEventListener('change', () => {
        group.hidden = !toggle.checked;
        if (!toggle.checked) {
            group.querySelectorAll('input[type="checkbox"]').forEach((child) => {
                child.checked = false;
            });
        }
    });
});

// My Account: on save, remember which section was on screen so the page comes
// back there (rather than the top), and show progress on the button.
document.querySelectorAll('[data-olt-account-form]').forEach((form) => {
    form.addEventListener('submit', () => {
        const sections = [...form.querySelectorAll('section[id]')];
        const middle = window.innerHeight / 2;
        const current = sections.filter((section) => section.getBoundingClientRect().top <= middle).pop() || sections[0];
        if (current && form.return_section) form.return_section.value = current.id;

        const button = form.querySelector('.olt-account-savebar__button');
        if (button) {
            button.disabled = true;
            button.firstChild.textContent = button.dataset.savingLabel;
        }
    });
});

// Events carousel arrows.
document.querySelectorAll('[data-olt-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-olt-carousel-track]');
    const prev = carousel.querySelector('[data-olt-carousel-prev]');
    const next = carousel.querySelector('[data-olt-carousel-next]');
    if (!track || !prev || !next) return;

    const update = () => {
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };
    const step = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });

    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', update, { passive: true });
    update();
});
